import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle2, Calendar, Users, ChevronDown, ChevronRight, Award, FileText, Paperclip, Download, Eye
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import TaskAttachments from '../components/TaskAttachments';
import api from '../utils/api';
import { toast } from 'sonner';

const MisTareasRealizadas = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTareas, setExpandedTareas] = useState({});
  const [showDocumentos, setShowDocumentos] = useState(false);
  const [selectedTarea, setSelectedTarea] = useState(null);

  // Función para contar todos los adjuntos de una tarea (directos + bitácora)
  const getTotalAdjuntos = (tarea) => {
    let total = 0;
    if (tarea.attachments?.length) total += tarea.attachments.length;
    if (tarea.gestiones?.length) {
      tarea.gestiones.forEach(g => {
        if (g.attachments?.length) total += g.attachments.length;
      });
    }
    return total;
  };

  useEffect(() => {
    fetchTareas();
  }, []);

  const fetchTareas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tasks/mis-tareas-realizadas');
      setTareas(response.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (taskId) => {
    setExpandedTareas(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Award className="w-10 h-10 text-primary" strokeWidth={1.5} />
              <h1 className="text-4xl font-bold text-slate-900" data-testid="mis-tareas-realizadas-title">
                Mis Tareas Realizadas
              </h1>
            </div>
            <p className="text-lg text-slate-600">
              Historial de tareas aprobadas por tu líder
            </p>
          </div>

          {/* Stats */}
          <Card className="p-4 mb-6 border-slate-200 bg-green-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-green-700">{tareas.length}</p>
                <p className="text-sm text-green-600">Tareas Completadas y Aprobadas</p>
              </div>
            </div>
          </Card>

          {/* Lista de tareas */}
          {loading ? (
            <Card className="p-12 text-center border-slate-200">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-600">Cargando historial...</p>
            </Card>
          ) : tareas.length === 0 ? (
            <Card className="p-12 text-center border-slate-200">
              <Award className="w-16 h-16 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-lg text-slate-600 mb-2">Aún no tienes tareas aprobadas</p>
              <p className="text-sm text-slate-500">
                Las tareas que completes y sean aprobadas por tu líder aparecerán aquí
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {tareas.map((tarea) => (
                <Card 
                  key={tarea.id} 
                  className="border-slate-200 overflow-hidden"
                  data-testid={`tarea-realizada-${tarea.id}`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <button 
                            onClick={() => toggleExpanded(tarea.id)}
                            className="p-1 hover:bg-slate-100 rounded"
                          >
                            {expandedTareas[tarea.id] ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                          </button>
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                            Aprobada
                          </span>
                          <span className="px-2 py-1 rounded text-xs bg-primary/10 text-primary">
                            {tarea.area}
                          </span>
                          {tarea.psp_folio && (
                            <span className="text-xs text-slate-500">
                              PSP: {tarea.psp_folio}
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-1">{tarea.title}</h3>
                        <p className="text-sm text-slate-600 mb-2">{tarea.description}</p>
                        
                        {/* Feedback del líder */}
                        {tarea.feedback_lider && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                            <p className="text-sm font-medium text-green-800 mb-1">Feedback del Líder:</p>
                            <p className="text-sm text-green-700">{tarea.feedback_lider}</p>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {tarea.client_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Aprobada: {tarea.fecha_aprobada ? new Date(tarea.fecha_aprobada).toLocaleDateString('es-CL') : 'N/A'}
                          </span>
                          {getTotalAdjuntos(tarea) > 0 && (
                            <button
                              onClick={() => {
                                setSelectedTarea(tarea);
                                setShowDocumentos(true);
                              }}
                              className="flex items-center gap-1 text-green-600 hover:text-green-700 font-medium"
                              data-testid={`ver-docs-${tarea.id}`}
                            >
                              <Paperclip className="w-4 h-4" />
                              {getTotalAdjuntos(tarea)} documento{getTotalAdjuntos(tarea) !== 1 ? 's' : ''}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        <CheckCircle2 className="w-8 h-8 text-green-500" />
                      </div>
                    </div>

                    {/* Historial completo de gestiones */}
                    <AnimatePresence>
                      {expandedTareas[tarea.id] && tarea.gestiones?.length > 0 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mt-4 pt-4 border-t border-slate-200"
                        >
                          <h4 className="text-sm font-medium text-slate-700 mb-2">Historial Completo</h4>
                          <div className="space-y-2">
                            {tarea.gestiones.map((gestion, idx) => (
                              <div 
                                key={idx} 
                                className={`rounded-lg p-3 text-sm ${
                                  gestion.tipo === 'aprobacion' 
                                    ? 'bg-green-50 border border-green-200'
                                    : gestion.tipo === 'rechazo'
                                    ? 'bg-red-50 border border-red-200'
                                    : gestion.tipo === 'completada'
                                    ? 'bg-blue-50 border border-blue-200'
                                    : 'bg-slate-50'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-slate-700">{gestion.usuario}</span>
                                  <span className="text-xs text-slate-500">
                                    {new Date(gestion.fecha).toLocaleString('es-CL')}
                                  </span>
                                </div>
                                <p className="text-slate-600">{gestion.descripcion}</p>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Modal Ver Documentos de la Tarea */}
      <Dialog open={showDocumentos} onOpenChange={setShowDocumentos}>
        <DialogContent className="max-w-4xl w-[90vw] lg:w-[70vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3649] text-lg font-semibold">
              <Paperclip className="w-5 h-5" />
              Documentos de la Tarea
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Todos los documentos del proceso (tuyos y de supervisores)
            </DialogDescription>
          </DialogHeader>
          {selectedTarea && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-800">{selectedTarea.title}</p>
                <p className="text-xs text-slate-600 mt-1">{selectedTarea.description?.slice(0, 150)}...</p>
              </div>
              
              {/* Componente de Adjuntos con historial completo */}
              <TaskAttachments
                entityType="task"
                entityId={selectedTarea.id}
                attachments={selectedTarea.attachments || []}
                canUpload={false}
                showBitacoraAttachments={true}
              />
              
              {/* Botón alineado a la derecha */}
              <div className='flex justify-end mt-6 pt-4 border-t border-slate-100'>
                <Button 
                  onClick={() => setShowDocumentos(false)}
                  variant="outline"
                  className='px-6'
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MisTareasRealizadas;
